import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../../lib/api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { chartTheme, tooltipStyle } from '../../lib/chartTheme';
import {
  Target, TrendingUp, Clock, Zap, BookOpen, Award, ArrowRight,
  Flame, ChevronRight, BarChart3, Brain, Sparkles, Play
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const fadeIn = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: 'easeOut' }
};

function KpiCard({ icon: Icon, label, value, subtitle, color = 'teal', delay = 0, testId }) {
  const colorClasses = {
    teal: 'text-teal-300 bg-teal-500/10 border-teal-500/20',
    amber: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    rose: 'text-rose-300 bg-rose-500/10 border-rose-500/20',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
    >
      <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-5 hover:border-slate-700 transition-colors" data-testid={testId}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-2xl font-semibold font-['Azeret_Mono'] text-white">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, [i18n.language]);

  const loadDashboard = async () => {
    try {
      const res = await userAPI.getDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ms) => {
    if (!ms) return '0m';
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-2xl bg-slate-800/50" />)}
        </div>
        <Skeleton className="h-64 rounded-2xl bg-slate-800/50" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-20 text-center">
        <Brain className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">{t('dashboard.emptyState', 'Start your first assessment to see learning insights')}</p>
        <Button onClick={() => navigate('/home')} className="bg-teal-500 text-slate-950 hover:bg-teal-400">
          <Play className="w-4 h-4 mr-2" /> {t('dashboard.startAssessment', 'Start Assessment')}
        </Button>
      </div>
    );
  }

  const { kpis, weekly_performance, strengths, weaknesses, recommendations, recent_assessments, comparison } = dashboard;

  // Week-over-week accuracy delta (e.g. "+4.2% vs last week")
  const accuracyDelta = comparison?.accuracy_delta;
  const accuracySubtitle = (accuracyDelta === null || accuracyDelta === undefined)
    ? t('dashboard.overallPerformance', 'Overall performance')
    : `${accuracyDelta >= 0 ? '▲ +' : '▼ '}${accuracyDelta}% ${t('dashboard.vsLastWeek', 'vs last week')}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 pb-8" data-testid="dashboard-page">
      {/* Header */}
      <motion.div {...fadeIn} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white font-['Space_Grotesk']"
              data-testid="dashboard-title">{t('dashboard.title', 'Learning Insights')}</h1>
          <p className="text-sm text-slate-400 mt-1">{t('dashboard.subtitle', 'Your personalized assessment analytics')}</p>
        </div>
        <Button
          onClick={() => navigate('/home')}
          className="bg-teal-500 text-slate-950 hover:bg-teal-400 rounded-xl font-semibold"
          data-testid="dashboard-start-assessment-button"
        >
          <Play className="w-4 h-4 mr-2" /> {t('dashboard.startAssessment', 'Start Assessment')}
        </Button>
      </motion.div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="kpi-row">
        <KpiCard
          icon={Target}
          label={t('kpi.mastery', 'Mastery')}
          value={`${kpis.mastery}%`}
          subtitle={t('kpi.assessmentsCount', '{{count}} assessments', { count: kpis.total_assessments })}
          color="teal"
          delay={0}
          testId="kpi-mastery"
        />
        <KpiCard
          icon={TrendingUp}
          label={t('kpi.accuracy', 'Accuracy')}
          value={`${kpis.overall_accuracy}%`}
          subtitle={accuracySubtitle}
          color="emerald"
          delay={0.05}
          testId="kpi-accuracy"
        />
        <KpiCard
          icon={Clock}
          label={t('kpi.avgTime', 'Avg. Time')}
          value={formatTime(kpis.avg_time_ms)}
          subtitle={t('kpi.perAssessment', 'Per assessment')}
          color="teal"
          delay={0.1}
          testId="kpi-time-on-task"
        />
        <KpiCard
          icon={Zap}
          label={t('kpi.xpThisWeek', 'XP This Week')}
          value={kpis.xp_week || 0}
          subtitle={`${t('dashboard.level', 'Level')} ${kpis.level} \u2022 ${kpis.level_title}`}
          color="amber"
          delay={0.15}
          testId="kpi-xp-week"
        />
      </div>

      {/* Main Grid: Chart + Strengths/Weaknesses */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Weekly Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-8"
        >
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="weekly-performance-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold tracking-tight text-white">{t('dashboard.weeklyPerformance', 'Weekly Performance')}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t('dashboard.accuracyTrend', 'Accuracy trend over 7 days')}</p>
              </div>
              <Badge variant="outline" className="text-teal-300 border-teal-500/30 bg-teal-500/10">
                <BarChart3 className="w-3 h-3 mr-1" /> {t('dashboard.trend', 'Trend')}
              </Badge>
            </div>
            <div className="h-[220px] sm:h-[260px] w-full">
              <ResponsiveContainer>
                <LineChart data={weekly_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: chartTheme.text, fontSize: 12 }}
                    axisLine={{ stroke: chartTheme.grid }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: chartTheme.text, fontSize: 12 }}
                    axisLine={{ stroke: chartTheme.grid }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip {...tooltipStyle} formatter={(v) => [`${v}%`, t('kpi.accuracy', 'Accuracy')]} />
                  <Line
                    type="monotone"
                    dataKey="accuracy"
                    stroke={chartTheme.teal}
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: chartTheme.teal, strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Strengths & Weaknesses */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="lg:col-span-4"
        >
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6 h-full" data-testid="strength-weakness-card">
            <h3 className="text-sm font-semibold tracking-tight text-white mb-4">{t('dashboard.skillDomains', 'Skill Domains')}</h3>
            
            {strengths.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> {t('dashboard.strengths', 'Strengths')}
                </p>
                <div className="space-y-2">
                  {strengths.slice(0, 3).map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300 truncate">{s.topic}</span>
                      <span className="text-xs font-['Azeret_Mono'] text-emerald-300">{s.accuracy}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {weaknesses.length > 0 && (
              <div>
                <p className="text-xs text-rose-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Target className="w-3 h-3" /> {t('dashboard.needsFocus', 'Needs Focus')}
                </p>
                <div className="space-y-2">
                  {weaknesses.slice(0, 3).map((w, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300 truncate">{w.topic}</span>
                      <span className="text-xs font-['Azeret_Mono'] text-rose-300">{w.accuracy}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {strengths.length === 0 && weaknesses.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">{t('dashboard.completeToSeeSkills', 'Complete assessments to see skill insights')}</p>
            )}
          </Card>
        </motion.div>
      </div>

      {/* Learning Path + Achievements Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Recommended Learning Path */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-7"
        >
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6" data-testid="learning-path-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold tracking-tight text-white">{t('dashboard.recommendedPath', 'Recommended Learning Path')}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t('dashboard.topicsNeedAttention', 'Topics that need your attention')}</p>
              </div>
              <Button
                onClick={() => navigate('/learning')}
                variant="ghost"
                size="sm"
                className="text-teal-400 hover:text-teal-300"
                data-testid="learning-path-module-cta"
              >
                {t('dashboard.viewAll', 'View All')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {recommendations.length > 0 ? (
              <div className="space-y-3">
                {recommendations.slice(0, 4).map((rec, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/40 hover:bg-slate-800/60 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      rec.priority === 'high' ? 'bg-rose-500/20 text-rose-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200 truncate">{rec.topic}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={rec.accuracy} className="h-1.5 flex-1 bg-slate-700" />
                        <span className="text-xs font-['Azeret_Mono'] text-slate-400">{rec.accuracy}%</span>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-xs flex-shrink-0 ${
                      rec.priority === 'high'
                        ? 'text-rose-300 border-rose-500/30 bg-rose-500/10'
                        : 'text-amber-300 border-amber-500/30 bg-amber-500/10'
                    }`}>
                      {t(`dashboard.priority.${rec.priority}`, rec.priority)}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkles className="w-10 h-10 text-teal-500/40 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{t('dashboard.noFocusAreas', 'Great job! No immediate focus areas')}</p>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Achievements / XP */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="lg:col-span-5"
        >
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6 h-full" data-testid="xp-level-card">
            <h3 className="text-sm font-semibold tracking-tight text-white mb-4">{t('dashboard.progressAchievements', 'Progress & Achievements')}</h3>
            
            {/* Level Progress */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-200">{t('dashboard.level', 'Level')} {kpis.level}</span>
                </div>
                <span className="text-xs text-amber-300/70">{kpis.level_title}</span>
              </div>
              <Progress value={kpis.level_progress || 0} className="h-2 bg-amber-900/30" />
              <p className="text-xs text-amber-400/60 mt-1.5 font-['Azeret_Mono']">
                {kpis.current_level_xp || 0} / {kpis.xp_for_next_level || 200} XP
              </p>
            </div>

            {/* Streak */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/40" data-testid="streak-indicator">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{t('dashboard.dayStreak', '{{count}} Day Streak', { count: kpis.streak_days || 0 })}</p>
                <p className="text-xs text-slate-500">{t('dashboard.bestStreak', 'Best: {{count}} days', { count: kpis.best_streak || 0 })}</p>
              </div>
            </div>

            {/* Total XP */}
            <div className="mt-4 text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">{t('dashboard.totalExperience', 'Total Experience')}</p>
              <p className="text-3xl font-bold font-['Azeret_Mono'] text-teal-300 mt-1">{kpis.xp_total || 0}</p>
              <p className="text-xs text-slate-500">{t('dashboard.xpEarned', 'XP earned')}</p>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Recent Assessments */}
      {recent_assessments && recent_assessments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 sm:p-6">
            <h3 className="text-sm font-semibold tracking-tight text-white mb-4">{t('dashboard.recentAssessments', 'Recent Assessments')}</h3>
            <div className="space-y-2">
              {recent_assessments.slice(0, 5).map((a, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/results/${a.quiz_index}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-teal-400">{a.quiz_index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{t('dashboard.assessmentN', 'Assessment {{n}}', { n: a.quiz_index + 1 })}</p>
                      <p className="text-xs text-slate-500">{a.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-['Azeret_Mono'] font-semibold ${
                      a.best_pct >= 80 ? 'text-emerald-400' : a.best_pct >= 50 ? 'text-amber-400' : 'text-rose-400'
                    }`}>{a.best_pct}%</span>
                    <ArrowRight className="w-4 h-4 text-slate-600" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
